import { useElementHeight } from "@ui/hooks/use-element-height";
import { createElement, type ReactElement, type RefObject } from "react";

/**
 * Viewport-pinned positioning shared by every fixed bottom shell (Dock,
 * BottomBar). Skin — fill, borders, safe-area padding, overflow shadow —
 * stays with each consumer.
 */
export const fixedBottomShellClasses = "fixed inset-x-0 bottom-0 z-(--z-chrome-bottom)";

export interface UseFixedBottomSpacerOptions {
  /** Whether page content overflows behind the shell — consumers toggle their border/shadow treatment on it. */
  hasOverflow?: boolean;
  /** Extra classes for the spacer (e.g. `md:hidden` when the shell itself hides at `md+`). */
  spacerClassName?: string;
}

export interface UseFixedBottomSpacerReturn<T extends HTMLElement> {
  /** Overflow state, passed through so the shell contract lives in one place. */
  hasOverflow: boolean;
  /** Measured shell height in px (0 until first measure). */
  height: number;
  /** Attach to the fixed shell so the spacer tracks its height. */
  ref: RefObject<T | null>;
  /** In-flow spacer reserving the shell's footprint so page content isn't hidden behind it. */
  spacer: ReactElement;
}

/**
 * Fixed-bottom-of-viewport shell contract shared by Dock and BottomBar:
 * measures the shell (via `useElementHeight`) and returns an in-flow spacer
 * div matching its height, plus the overflow state that drives the shells'
 * border/shadow toggle.
 */
export function useFixedBottomSpacer<T extends HTMLElement = HTMLDivElement>({
  hasOverflow = false,
  spacerClassName,
}: UseFixedBottomSpacerOptions = {}): UseFixedBottomSpacerReturn<T> {
  const { ref, height } = useElementHeight<T>();

  const spacer = createElement("div", {
    "aria-hidden": true,
    className: spacerClassName,
    style: { height: height || undefined },
  });

  return { hasOverflow, height, ref, spacer };
}
